import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiArrowLeft, FiInfo, FiPrinter } from 'react-icons/fi';

import legalService from '../../services/api/legalService';

/**
 * Legal documents — one page for the catalogue (/legal) and one document
 * (/legal/:slug). Reachable signed-in from every role area and signed-out for
 * the documents that must be public, so it must not assume an authenticated
 * layout around it.
 *
 * Each document carries both languages: Danish first, then English, exactly as
 * the signed artifact reads. The body is structured JSON rendered as JSX — the
 * app has no markdown renderer and deliberately never uses
 * dangerouslySetInnerHTML, so the text stays escaped by React.
 */

/* ---------- layout ---------- */

const Page = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--text-sm, 0.9rem);
  color: var(--act-primary, #17627f);
  text-decoration: none;
  margin-bottom: 16px;

  &:hover { text-decoration: underline; }
`;

const DocTitle = styled.h1`
  font-size: 1.6rem;
  color: var(--ve-ink-800, #19242e);
  margin: 0 0 4px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  color: var(--ve-ink-500, #5a6570);
  font-size: var(--text-xs, 0.78rem);
  margin-bottom: 20px;
`;

const LangNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const LangJump = styled.a`
  border: 1px solid var(--act-primary, #17627f);
  border-radius: var(--app-radius, 6px);
  color: var(--act-primary, #17627f);
  text-decoration: none;
  padding: 5px 12px;
  font-size: var(--text-sm, 0.9rem);

  &:hover { background: rgba(23, 98, 127, 0.08); }
`;

const PrintButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: transparent;
  border-radius: var(--app-radius, 6px);
  padding: 5px 12px;
  font-size: var(--text-sm, 0.9rem);
  color: var(--ve-ink-600, #4a5560);
  cursor: pointer;

  &:hover { border-color: var(--act-primary, #17627f); color: var(--act-primary, #17627f); }

  @media print { display: none; }
`;

/* ---------- document body ---------- */

const LangBlock = styled.section`
  & + & {
    margin-top: 56px;
    padding-top: 32px;
    border-top: 3px solid var(--ve-ink-800, #19242e);
  }
`;

const LangHeading = styled.h2`
  font-size: 1.3rem;
  color: var(--ve-ink-800, #19242e);
  margin: 0 0 20px;
`;

const Section = styled.section`
  margin-bottom: 26px;
  scroll-margin-top: 16px;
`;

const SectionHeading = styled.h3`
  font-size: 1.02rem;
  color: var(--ve-ink-800, #19242e);
  margin: 0 0 8px;
`;

const SubHeading = styled.h4`
  font-size: 0.94rem;
  color: var(--ve-ink-800, #19242e);
  margin: 16px 0 6px;
`;

const Para = styled.p`
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--ve-ink-700, #333333);
  margin: 0 0 10px;
`;

const List = styled.ul`
  margin: 0 0 10px;
  padding-left: 22px;

  li {
    font-size: 0.92rem;
    line-height: 1.55;
    color: var(--ve-ink-700, #333333);
    margin-bottom: 4px;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 0 0 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;

  th, td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 7px 9px;
    text-align: left;
    vertical-align: top;
    line-height: 1.45;
  }

  th {
    background: var(--surface-subtle, #f5f6f7);
    color: var(--ve-ink-700, #333333);
    font-weight: 600;
  }

  td { color: var(--ve-ink-700, #333333); }
`;

const TONES = {
  warn: { border: '#d4ac0d', background: '#fef9e7', icon: FiAlertTriangle },
  danger: { border: 'var(--act-danger, #c3251d)', background: '#fdedec', icon: FiAlertTriangle },
  info: { border: 'var(--act-primary, #17627f)', background: '#eef4f7', icon: FiInfo },
};

const Note = styled.div`
  border-left: 4px solid ${({ $tone }) => (TONES[$tone] || TONES.info).border};
  background: ${({ $tone }) => (TONES[$tone] || TONES.info).background};
  border-radius: 0 var(--app-radius, 6px) var(--app-radius, 6px) 0;
  padding: 11px 14px;
  margin: 0 0 14px;
`;

const NoteTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ve-ink-800, #19242e);
  margin-bottom: 5px;
`;

const NoteText = styled.div`
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--ve-ink-700, #333333);
`;

/* ---------- catalogue ---------- */

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled(Link)`
  display: block;
  background: var(--app-surface, #fff);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: var(--app-radius, 6px);
  padding: 14px 16px;
  text-decoration: none;

  &:hover { border-color: var(--act-primary, #17627f); }
`;

const CardTitle = styled.div`
  font-weight: 600;
  color: var(--ve-ink-800, #19242e);
  margin-bottom: 3px;
`;

const CardMeta = styled.div`
  font-size: var(--text-xs, 0.78rem);
  color: var(--ve-ink-500, #5a6570);
  margin-top: 6px;
`;

const CardSummary = styled.div`
  font-size: 0.88rem;
  color: var(--ve-ink-600, #4a5560);
  line-height: 1.5;
`;

const Status = styled.div`
  padding: 24px 0;
  color: var(--ve-ink-500, #5a6570);
  font-size: 0.92rem;
`;

/* ---------- block renderer ---------- */

const renderBlock = (block, index) => {
  switch (block.type) {
    case 'p':
      return <Para key={index}>{block.text}</Para>;
    case 'h3':
      return <SubHeading key={index}>{block.text}</SubHeading>;
    case 'ul':
      return (
        <List key={index}>
          {(block.items || []).map((item, i) => <li key={i}>{item}</li>)}
        </List>
      );
    case 'ol':
      return (
        <List as="ol" key={index}>
          {(block.items || []).map((item, i) => <li key={i}>{item}</li>)}
        </List>
      );
    case 'table':
      return (
        <TableWrap key={index}>
          <Table>
            <thead>
              <tr>{(block.head || []).map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {(block.rows || []).map((row, r) => (
                <tr key={r}>{row.map((cell, c) => <td key={c}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      );
    case 'note': {
      const Icon = (TONES[block.tone] || TONES.info).icon;
      return (
        <Note key={index} $tone={block.tone}>
          {block.title && <NoteTitle><Icon />{block.title}</NoteTitle>}
          <NoteText>{block.text}</NoteText>
        </Note>
      );
    }
    default:
      // Unknown block types are dropped rather than crashing a legal page the
      // user may be required to read. The backend validates on load, so this is
      // only reachable if a document is edited without updating the renderer.
      return null;
  }
};

const LegalDocuments = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [catalogue, setCatalogue] = useState(null);
  const [document_, setDocument] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Clear the other slot: React Router reuses this component instance
      // across /legal and /legal/:slug, so without this the previous route's
      // data paints for one render.
      if (slug) {
        setCatalogue(null);
        setDocument(await legalService.getDocument(slug));
      } else {
        setDocument(null);
        setCatalogue(await legalService.listDocuments());
      }
    } catch (err) {
      const code = err && err.response && err.response.data
        && err.response.data.error && err.response.data.error.code;
      setError(code === 'AUTHENTICATION_REQUIRED' ? t('legal.signInRequired') : t('legal.loadError'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Page><Status>{t('common.loading')}</Status></Page>;

  if (error) {
    return (
      <Page>
        <BackLink to="/legal"><FiArrowLeft />{t('legal.backToList')}</BackLink>
        <Status>{error}</Status>
      </Page>
    );
  }

  // Catalogue
  if (!slug) {
    return (
      <Page>
        <DocTitle>{t('legal.pageTitle')}</DocTitle>
        <MetaRow>{t('legal.listIntro')}</MetaRow>
        <CardList>
          {(catalogue || []).map((doc) => (
            <Card key={doc.slug} to={`/legal/${doc.slug}`}>
              <CardTitle>{doc.title && (doc.title.da || doc.title.en)}</CardTitle>
              {doc.summary && <CardSummary>{doc.summary.da || doc.summary.en}</CardSummary>}
              <CardMeta>
                {t('legal.version', { version: doc.version })}
                {doc.effectiveDate ? ` · ${t('legal.effective', { date: doc.effectiveDate })}` : ''}
              </CardMeta>
            </Card>
          ))}
        </CardList>
      </Page>
    );
  }

  // One document, both languages. document_ is still null on the first render
  // after navigating /legal -> /legal/:slug: React Router does not key route
  // elements, so this instance and its state are reused and `slug` changes one
  // render before the effect can refetch. Without this guard that render throws
  // and the root error boundary replaces the whole app.
  if (!document_) return <Page><Status>{t('common.loading')}</Status></Page>;

  const languages = document_.documents || [];
  return (
    <Page>
      <BackLink to="/legal"><FiArrowLeft />{t('legal.backToList')}</BackLink>
      <DocTitle>{document_.title && (document_.title.da || document_.title.en)}</DocTitle>
      <MetaRow>
        <span>{t('legal.version', { version: document_.version })}</span>
        {document_.effectiveDate && <span>{t('legal.effective', { date: document_.effectiveDate })}</span>}
      </MetaRow>

      <LangNav>
        {languages.map((lang) => (
          <LangJump key={lang.lang} href={`#doc-${lang.lang}`}>{lang.title}</LangJump>
        ))}
        <PrintButton type="button" onClick={() => window.print()}>
          <FiPrinter />{t('legal.print')}
        </PrintButton>
      </LangNav>

      {languages.map((lang) => (
        <LangBlock key={lang.lang} id={`doc-${lang.lang}`}>
          <LangHeading>{lang.title}</LangHeading>
          {!!(lang.meta && lang.meta.length) && (
            <MetaRow>
              {lang.meta.map((m, i) => <span key={i}>{m.label}: {m.value}</span>)}
            </MetaRow>
          )}
          {(lang.sections || []).map((section) => (
            <Section key={section.id} id={`${lang.lang}-${section.id}`}>
              <SectionHeading>{section.heading}</SectionHeading>
              {(section.blocks || []).map(renderBlock)}
            </Section>
          ))}
        </LangBlock>
      ))}
    </Page>
  );
};

export default LegalDocuments;
